import { Fragment, ReactNode, memo, useEffect, useState } from "react";
import usePurchases from "../../hooks/usePurchases";
import CheckboxElement from "../../../../Elements/CheckboxElement/CheckboxElement";
import InputElement from "../../../../Elements/InputElement/InputElement";
import { useContent } from "../../../../../contexts/contentContext/contentContext";
import Button from "../../../../Elements/Button/Button";
import useCurrency from "../../hooks/useCurrency";
import { CurrencySymbolE, PurchaseStepE } from "../../models/enums";
import { useParams, useSearchParams } from "react-router-dom";
import DateCounter from "./components/DateCounter";
import Image from "../../../../Elements/Image/Image";
import useCouponCode from "../../hooks/useCouponCode";
import type { PeriodTypeT, PurchaseParamsT } from "../../models/models";
import useTabFocus from "../../../../../hooks/useTabFocus";

import styles from "./styles.module.scss";

const EMPTY_FIELD_ERROR_CODE = "7785758";
const FULL_DISCOUNT = 100;

const PurchaseCouponSection = memo(() => {
  const {
    packageType,
    couponCode: couponCodeParam,
    periodType,
  } = useParams<PurchaseParamsT>();
  const { getDirection, getTextByKey } = useContent();
  const { selectedPlanPrice, setCompletedStep, completedStep } = usePurchases();
  const {
    validateCoupon,
    couponCode,
    setCouponCode,
    createTransactionFreeCoupon,
    isCreatingTransaction,
    clearCouponCodeData,
    selectedPlanPriceWithDiscount,
  } = useCouponCode();
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();
  const [isCouponFeatureEnabled, setIsCouponFeatureEnabled] = useState(
    !!couponCodeParam || !!searchParams.get("coupon")
  );

  const focusableProps = useTabFocus(
    () => setIsCouponFeatureEnabled((prev) => !prev),
    styles.focusedCheckbox
  );

  useEffect(() => {
    if (!isCouponFeatureEnabled) {
      clearCouponCodeData();
    }
  }, [clearCouponCodeData, isCouponFeatureEnabled]);

  // Go to primary step if checkbox clicked or input changed
  useEffect(() => {
    if (isCouponFeatureEnabled) {
      setCompletedStep(PurchaseStepE.PRIMARY_PURCHASE_STEP);
    }
  }, [isCouponFeatureEnabled, couponCode?.code]);

  // Collapse coupon section if coupon code is not provided and user went to the next step
  useEffect(() => {
    if (
      !couponCode?.code &&
      completedStep !== PurchaseStepE.PRIMARY_PURCHASE_STEP
    ) {
      setIsCouponFeatureEnabled(false);
    }
  }, [couponCode?.code, completedStep]);

  return (
    <div
      className={`${styles.couponDetailsSection} ${
        isCouponFeatureEnabled ? styles.displayCouponInput : ""
      } ${
        packageType !== "pay-as-you-go" &&
        packageType !== "sub-users" &&
        packageType !== "extra-storage"
          ? styles.displayCouponContainer
          : styles.hideCoupon
      }`}
    >
      <div {...focusableProps}>
        <CheckboxElement
          state={isCouponFeatureEnabled}
          setState={setIsCouponFeatureEnabled}
          checkboxLabel={"use-coupon"}
          checkboxTooltip={"use-coupon-tooltip"}
        />
      </div>
      <div className={styles.couponInputDiv}>
        {isCouponFeatureEnabled ? (
          <div className={styles.couponInputContent}>
            <div className={styles.couponInput}>
              {/*TODO: Need to create types for InputElement.js*/}
              {/*@ts-ignore*/}
              <InputElement
                disabled={couponCode.status === "validating"}
                specificClass={
                  couponCode?.isValid === false
                    ? styles.invalidCoupon
                    : couponCode?.isValid === true
                    ? styles.validCoupon
                    : ""
                }
                state={couponCode?.code}
                setState={setCouponCode}
              />
              {couponCode?.isValid === true ? (
                <div className={styles.couponDiscountDetails}>
                  <span className={styles.currentPrice} dir={getDirection()}>
                    {selectedPlanPrice}
                    {CurrencySymbolE[currency]}
                  </span>
                  <div className={styles.newPrice}>
                    <span className={styles.newPriceText}>
                      {getTextByKey("price-after-discount")}
                    </span>
                    <span className={styles.newPriceSpan} dir={getDirection()}>
                      {selectedPlanPriceWithDiscount}
                      {CurrencySymbolE[currency]}
                    </span>
                  </div>
                </div>
              ) : (
                <Button
                  className="new-button"
                  onClick={() => validateCoupon(periodType as PeriodTypeT)}
                  disabled={couponCode.status === "validating"}
                >
                  {getTextByKey("verify-coupon")}
                  {couponCode.status === "validating" ? (
                    <Image name="preloader" ext="gif" />
                  ) : null}
                </Button>
              )}
            </div>
            {couponCode?.isValid === false ? (
              <div className={styles.invalidCouponDivSpan}>
                <span className={styles.invalidCouponSpan}>
                  {
                    // Handle empty field case
                    couponCode?.validationText?.includes(EMPTY_FIELD_ERROR_CODE)
                      ? getTextByKey("required-field")
                      : couponCode?.validationText
                  }
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
        {couponCode?.expireDate ? <DateCounter /> : null}
      </div>
      {couponCode?.discount === FULL_DISCOUNT ? (
        <div className={styles.freeCoupon}>
          <Button
            className={styles.newButton}
            onClick={createTransactionFreeCoupon}
            disabled={isCreatingTransaction}
          >
            {isCreatingTransaction ? (
              <Image
                name="preloader"
                ext="gif"
                className="loader-animation-gif"
              />
            ) : (
              getTextByKey("purchase-next")
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
});

export default PurchaseCouponSection;
